import {Component, HostListener, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {ActivatedRoute, Router} from '@angular/router';
import {DokumentModels} from 'src/app/models/dokument-models';
import {ServiceAppModels} from 'src/app/models/serviceapp-models';
import {DatumFormatPipe} from 'src/app/pipes/datum-format.pipe';
import {DokumentDataService} from 'src/app/services/dokument-data.service';
import {MessageBoxService} from 'src/app/services/message-box.service';
import {ServiceAppService} from 'src/app/services/service-app.service';
import {ServiceAppComponent} from '../service-app.component';
import {UserAuthenticationService} from 'src/app/services/user-authentication.service';
import {MailPreviewDialogComponent, MailPreviewDialogData} from '../../../swiss-forms/mail-preview-dialog/mail-preview-dialog.component';
import MsgReader from '@kenjiuno/msgreader';
import {decompressRTF} from '@kenjiuno/decompressrtf';
import iconv from 'iconv-lite';
import {deEncapsulateSync} from 'rtf-stream-parser';
import {DomSanitizer} from '@angular/platform-browser';
import {HelperService} from '../../../services/helper.service';
import {SwissSettingsService} from '../../../services/swiss-settings.service';
import {BehaviorSubject} from 'rxjs';
import TerminDokumentListElement = ServiceAppModels.TerminDokumentListElement;
import DokumentInfo = DokumentModels.DokumentInfo;
import DokumentTyp = DokumentModels.DokumentTyp;
import DokumentSource = DokumentModels.DokumentSource;

@Component({
    selector: 'app-docs', templateUrl: './docs.component.html', styleUrls: ['./docs.component.scss'],
})
export class DocsComponent implements OnInit {

    public currTermin?: ServiceAppModels.SA_Objekt;
    public isVinaIntern: boolean;
    protected dataSourceServiceAppDokus: MatTableDataSource<ServiceAppModels.TerminDokumentListElement>;
    protected hasSADokus$ = new BehaviorSubject(false);
    protected displayedDokuColumns: string[] = ['doku_art', 'doku_bearbeitet', 'doku_desc', 'doku_name', 'doku_ansprechname', 'doku_creator', 'doku_settings'];
    protected selectedDocumentID = '';
    protected canHaveCheckList$ = new BehaviorSubject(false);
    private terminDokumente: ServiceAppModels.TerminDokumentListElement[] = [];
    private key: any;

    constructor(public swissSettings: SwissSettingsService,
                private sanitizer: DomSanitizer,
                private datumFormatPipe: DatumFormatPipe,
                private dokuData: DokumentDataService,
                private saComp: ServiceAppComponent,
                private userAuthService: UserAuthenticationService,
                private svcMessageBox: MessageBoxService,
                public dialog: MatDialog,
                private router: Router,
                private route: ActivatedRoute,
                private svcSA: ServiceAppService,
                private helperService: HelperService) {
        this.dataSourceServiceAppDokus = new MatTableDataSource<ServiceAppModels.TerminDokumentListElement>();
        this.helperService.setPageRefreshURL('/serviceapp/sadocs');
        this.isVinaIntern = this.swissSettings.getSwissAppConfig()?.vinaIntern || false;
    }

    @HostListener('document:keydown', ['$event']) handleKeyboardEvent(event: KeyboardEvent): void {
        this.key = event.key;
    }

    ngOnInit(): void {
        this.currTermin = this.svcSA.getTermin();

        this.route.params.subscribe({
            next: (params) => {
                this.currTermin = this.svcSA.getTermin();
                this.saComp.reloadAppointment(this.currTermin);
            },
        });
        this.loadDocuments();
    }

    loadDocuments(): void {
        const user = this.userAuthService.getCurrentAuthenticatedUser();
        if (!this.currTermin || !this.currTermin.serviceID || !this.currTermin.objectID || !user) {
            return;
        }
        // Via Webservice-Stammdaten Dokumente ermitteln
        this.terminDokumente = this.svcSA.getTerminDokumenteList(this.currTermin.serviceID, this.currTermin.objectID);
        this.hasSADokus$.next(this.terminDokumente.length > 0);

        this.dataSourceServiceAppDokus.data = this.terminDokumente.sort((a, b) => (a.datum < b.datum && 1) || -1);

        this.canHaveCheckList$.next(
            this.svcSA.existsCheckliste(user.mandatorID)
            && !this.svcSA.existsChecklistenByServiceID(this.currTermin.serviceID)
        );
    }

    formatDate(datum: Date): string {
        // Formatiert das Datum in das Format "dd.MM.yyy HH.mm.ss"
        return this.datumFormatPipe.transform(datum);
    }

    downloadDocument(row: any): void {
        const docID = row.id;
        const terminDokument = this.terminDokumente.find((doc) => doc.id === docID);

        if (!terminDokument) {
            throw Error('Document could not be found');
        }

        let fileName = terminDokument.documentName;

        // Wenn es sich um eine s3a-Datei handelt, muss die Dateierweiterung auf pdf geändert werden.
        // Beispiel: test.s3a wird zu test.pdf
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
        if (fileExtension.toLowerCase() === '.s3a') {
            fileName = fileName.substring(0, fileName.lastIndexOf('.')) + '.pdf';
        }

        if (fileName) {
            this.dokuData.downloadDocument(docID, fileName);
        }
    }

    async openDocument(row: TerminDokumentListElement): Promise<void> {
        if (!this.currTermin) {
            return;
        }

        const docID = row.id;

        const terminDokument = this.terminDokumente.find((doc) => doc.id === docID);
        if (!terminDokument) {
            return;
        }
        // Hier wird der Dateiname anhand der DocumentID ermittelt
        let fileName = terminDokument.documentName;

        // Wenn es sich um eine s3a-Datei handelt, muss die Dateierweiterung auf pdf geändert werden.
        // Beispiel: test.s3a wird zu test.pdf
        const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
        if (fileExtension.toLowerCase() === '.s3a') {
            fileName = fileName.substring(0, fileName.lastIndexOf('.')) + '.pdf';
        }

        if (fileName) {
            switch (fileExtension.toLowerCase()) {
                case '.pdf':
                    const pdfList = this.getPDFElements();
                    if ((row as ServiceAppModels.TerminDokumentListElement).dokumentTyp === DokumentModels.DokumentTyp.Rapport) {
                        this.dokuData.setPDFDokument(this.currTermin.objectID, docID, fileName, '', DokumentModels.DokumentTyp.Rapport, terminDokument.documentQuelle, row.lastModified, pdfList);
                    } else if ((row as ServiceAppModels.TerminDokumentListElement).dokumentTyp === DokumentModels.DokumentTyp.Checkliste) {
                        this.dokuData.setPDFDokument(this.currTermin.objectID, docID, fileName, '', DokumentModels.DokumentTyp.Checkliste, terminDokument.documentQuelle, row.lastModified, pdfList);
                    } else {
                        this.dokuData.setPDFDokument(this.currTermin.objectID, docID, fileName, terminDokument.description, DokumentModels.DokumentTyp.Dokument, terminDokument.documentQuelle, row.lastModified, pdfList);
                    }
                    this.openPDFViewer(docID);
                    break;
                case '.msg':
                    const data = await this.dokuData.getDocumentBlob(docID);
                    const dialogData: MailPreviewDialogData = {
                        isHTML: false, text: ''
                    };
                    const buffer = await data.arrayBuffer();
                    const testMsg = new MsgReader(buffer);
                    const compressedRtf = testMsg.getFileData().compressedRtf;
                    if (compressedRtf) {
                        const rtfBlob = decompressRTF(Array.from(compressedRtf));
                        const stream = iconv.decode(Buffer.from(rtfBlob), 'latin1');
                        const result = deEncapsulateSync(stream, {decode: iconv.decode});

                        dialogData.isHTML = (result.mode === 'html');
                        dialogData.text = this.sanitizer.bypassSecurityTrustHtml(this.blankATags(result.text as string) || '');
                    } else {
                        if (testMsg.getFileData().bodyHtml) {
                            dialogData.isHTML = true;
                            dialogData.text = this.sanitizer.bypassSecurityTrustHtml(this.blankATags(testMsg.getFileData().bodyHtml || '') || '');
                        }
                    }
                    this.dialog.open(MailPreviewDialogComponent, {
                        data: dialogData
                    });
                    break;
                default:
                    this.dokuData.downloadDocument(docID, fileName);
            }
        }
    }

    async makeChecklist(): Promise<void> {
        if (!this.currTermin) {
            this.svcMessageBox.showInfoByLangagueKey('MAIN_APP_SERVICE_APP.noAppointmentSelected', '');
            return;
        }
        this.canHaveCheckList$.next(false);
        const checkListUploadElement = await this.svcSA.saveCheckliste(this.currTermin.serviceID, this.currTermin.objectID);
        if (checkListUploadElement) {
            const pdfList = this.getPDFElements();
            this.dokuData.setPDFDokument(this.currTermin.objectID, checkListUploadElement.documentID, checkListUploadElement.fileName, '', DokumentModels.DokumentTyp.Checkliste, DokumentModels.DokumentSource.SA_DBChecklisten, new Date(), pdfList);
            try {
                const res = await this.svcSA.doUploadCheckliste(checkListUploadElement);
                if (res) {
                    this.openPDFViewer(checkListUploadElement.documentID);
                } else {
                    this.svcMessageBox.showInfo('Checkliste konnte nicht hochgeladen werden!', '!! Info !!');
                    this.openPDFViewer(checkListUploadElement.documentID);
                }
            } catch (err) {
                this.loadDocuments();
                throw err;
            }
        }
    }

    async deleteDoku(element: ServiceAppModels.TerminDokumentListElement, event: Event): Promise<void> {
        // Mit CTRL kann das Dokument gelöscht werden
        if (this.key !== 'Control' && element.dokumentTyp === DokumentModels.DokumentTyp.Checkliste) {
            this.svcMessageBox.showError('Eine erstellte Checkliste darf im Vinavon Service nicht gelöscht werden. ', '!! FEHLER !!');
        } else if (this.key !== 'Control' && element.dokumentTyp === DokumentModels.DokumentTyp.Rapport) {
            this.svcMessageBox.showError('Ein erstellter Rapport darf im Vinavon Service nicht gelöscht werden. ', '!! FEHLER !!');
        } else {
            this.svcMessageBox
                .askYesNoQuestion('MAIN_APP_DIALOG.DEL_DOCUMENT.msg', 'MAIN_APP_DIALOG.DEL_DOCUMENT.title', [element.documentName])
                .then((value) => {
                    if (value) {
                        this.svcSA.delDokument(element.id).then((res) => {
                            if (res) {
                                this.loadDocuments();
                            } else {
                                console.error('Das Dokument konnte nicht gelöscht werden.', element);
                                this.svcMessageBox.showError('Das Dokument konnte nicht gelöscht werden. ', '!! FEHLER !!');
                            }
                            event.stopPropagation(); // sonst wird der Row-Click-Event noch ausgeführt noch ausgeführt
                        });
                    }
                });
        }
    }

    private blankATags(htmlString: string): string {
        const div = document.createElement('div');
        div.innerHTML = htmlString.trim();
        Array.from(div.getElementsByTagName('a')).forEach(element => {
            element.target = '_blank';
        });
        return div.innerHTML;
    }

    private openPDFViewer(docID): void {
        this.router.navigate(['pdfviewer', docID]);
    }

    private getPDFElements(): DokumentInfo[] {
        const id = this.currTermin?.objectID;
        if (!id) {
            throw Error('TerminID is not set');
        }
        return this.dataSourceServiceAppDokus.data.filter(terminActivity => terminActivity.imageFile === 'pdf.png').flatMap<DokumentInfo>(pdfActivity => {
            return {
                objectID: id,
                documentID: pdfActivity.id,
                description: pdfActivity.description,
                fileName: pdfActivity.documentName,
                dokumentTyp: DokumentTyp.Notizblatt,
                dokumentQuelle: DokumentSource.FromWebService,
                lastModified: new Date()
            };
        });
    }
}
